import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useNavigate } from "react-router-dom";
import { profileService, authService, formatDetail } from "@/services";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trophy, Star, Target, Zap, TrendingUp, Award, ArrowLeft, Download, Trash2, ShieldOff, BadgeCheck, MailWarning, User } from "lucide-react";
import AccountNavMenu from "@/components/AccountNavMenu";
import BadgesGrid from "@/components/BadgesGrid";
import StreakChip from "@/components/StreakChip";
import { toast } from "@/components/ui/sonner";

const LEVEL_COLORS = {
  Beginner: "bg-gray-100 text-gray-700",
  Trader: "bg-blue-100 text-blue-700",
  Whale: "bg-purple-100 text-purple-700",
  "Market Master": "bg-amber-100 text-amber-700",
};

const MISSION_LABELS = {
  win_3_games: { labelKey: "profile.missions.win3", icon: Trophy },
  trigger_2_melee: { labelKey: "profile.missions.trigger2Melee", icon: Zap },
  earn_500_match: { labelKey: "profile.missions.earn500", icon: TrendingUp },
  play_5_games: { labelKey: "profile.missions.play5", icon: Target },
};

export default function ProfilePage() {
  const { t } = useTranslation();
  usePageTitle("Profile");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);

  useEffect(() => {
    if (user) {
      profileService.getProfile(user.user_id)
        .then(setProfile)
        .catch(() => {});
    }
  }, [user]);

  if (!user) {
    navigate("/login");
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleLogoutEverywhere = async () => {
    if (loggingOutAll) return;
    const confirmed = window.confirm(
      t("profile.verification.logoutEverywhereConfirm"),
    );
    if (!confirmed) return;
    setLoggingOutAll(true);
    try {
      await authService.logoutEverywhere();
      navigate("/login");
    } catch (err) {
      console.error("logout-all failed", err);
    } finally {
      setLoggingOutAll(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await authService.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `marketmelee-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("export failed", err);
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleteError("");
    if (confirmText !== "DELETE") {
      setDeleteError(t("profile.deleteModal.errors.typeDelete"));
      return;
    }
    setDeleting(true);
    try {
      await authService.deleteAccount("DELETE", confirmPassword);
      setShowDeleteModal(false);
      navigate("/login");
    } catch (err) {
      const msg = formatDetail(err?.response?.data?.detail) || t("profile.deleteModal.errors.default");
      setDeleteError(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] p-4 md:p-8" id="main-content" role="main" tabIndex={-1} data-testid="profile-page">
      <div className="max-w-2xl mx-auto">
        {/* Two-row header (back+menu, then page title) — matches the
            Leaderboard / Settings / Season pages so all account-y
            pages share the same shape. */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard" className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t("profile.navigation.back")}
          </Button>
          <AccountNavMenu current="profile" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A237E] dark:text-[#E3F2FD] flex items-center gap-2 mb-6" style={{ fontFamily: "Fredoka" }}>
          <User className="w-6 h-6 text-[#38B6FF] shrink-0" />
          {t("lobby.nav.profile")}
        </h1>

        {/* Profile Header */}
        <div className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6 mb-6">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-16 h-16 rounded-full border-2 border-[#38B6FF]" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#38B6FF] flex items-center justify-center text-white text-2xl font-bold" style={{ fontFamily: "Fredoka" }}>
                {user.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }}>{user.name}</h1>
              <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                <p className="text-sm text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{user.email}</p>
                <EmailVerificationBadge user={user} t={t} />
              </div>
              {profile && (
                <Badge className={`mt-1 ${LEVEL_COLORS[profile.level_name] || "bg-gray-100"}`} data-testid="level-badge">
                  <Star className="w-3 h-3 mr-1" /> {t("profile.level", { level: profile.level, name: profile.level_name })}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {profile && (
          <>
            {/* Sign-in streak — flame chip + 7-day grid. Lives here
                rather than the dashboard so the dashboard stays
                focused on play CTAs and progress sits with the rest
                of the user's stats. */}
            <div
              className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6 mb-6 flex justify-center"
              data-testid="profile-streak-block"
            >
              <StreakChip testId="profile-streak" />
            </div>

            {/* XP Bar */}
            <div className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6 mb-6">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-xs font-semibold text-[#1A237E]/50 dark:text-[#E3F2FD]/50 uppercase tracking-wider">{t("profile.stats.xpProgress")}</p>
                  <p className="text-2xl font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }} data-testid="xp-total">{profile.xp.toLocaleString()} <span className="text-sm font-semibold text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{t("profile.stats.xp")}</span></p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{t("profile.stats.toNextLevel")}</p>
                  <p className="text-sm font-bold text-[#38B6FF]">{t("profile.stats.xpNeeded", { count: 500 - (profile.xp % 500) })}</p>
                </div>
              </div>
              <div className="h-4 w-full bg-[#1A237E]/10 dark:bg-[#E3F2FD]/10 rounded-full overflow-hidden mb-4">
                <div
                  className="h-full bg-gradient-to-r from-[#FFC107] to-[#FF9800] transition-all duration-1000 ease-out rounded-full"
                  style={{ width: `${Math.min(100, (profile.xp % 500) / 5)}%` }}
                  data-testid="xp-bar"
                />
              </div>

              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-[#E3F2FD] dark:bg-[#0a1428] rounded-2xl p-3">
                  <Trophy className="w-5 h-5 mx-auto mb-1 text-[#FFC107]" />
                  <p className="text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }} data-testid="stat-wins">{profile.wins}</p>
                  <p className="text-xs text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{t("profile.stats.wins")}</p>
                </div>
                <div className="bg-[#E3F2FD] dark:bg-[#0a1428] rounded-2xl p-3">
                  <Target className="w-5 h-5 mx-auto mb-1 text-[#38B6FF]" />
                  <p className="text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }} data-testid="stat-games">{profile.games_played}</p>
                  <p className="text-xs text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{t("profile.stats.games")}</p>
                </div>
                <div className="bg-[#E3F2FD] dark:bg-[#0a1428] rounded-2xl p-3">
                  <TrendingUp className="w-5 h-5 mx-auto mb-1 text-[#00E676]" />
                  <p className="text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }} data-testid="stat-best">${profile.highest_portfolio}</p>
                  <p className="text-xs text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{t("profile.stats.best")}</p>
                </div>
              </div>

              {/* Extended stats (#6). Render as a second 3-up row so
                  the existing wins/games/best card layout stays
                  visually anchored. Each tile is self-contained — if
                  a stat is empty (no games played, no favorite yet),
                  it shows a dash so the grid keeps even spacing. */}
              <div className="grid grid-cols-3 gap-3 text-center mt-3">
                <div className="bg-[#E3F2FD] dark:bg-[#0a1428] rounded-2xl p-3">
                  <Award className="w-5 h-5 mx-auto mb-1 text-[#8247E5]" />
                  <p
                    className="text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD]"
                    style={{ fontFamily: "Fredoka" }}
                    data-testid="stat-winrate"
                  >
                    {profile.games_played ? `${Math.round((profile.win_rate || 0) * 100)}%` : "-"}
                  </p>
                  <p className="text-xs text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{t("profile.stats.winRate")}</p>
                </div>
                <div className="bg-[#E3F2FD] dark:bg-[#0a1428] rounded-2xl p-3">
                  <TrendingUp className="w-5 h-5 mx-auto mb-1 text-[#FF9F1C]" />
                  <p
                    className="text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD]"
                    style={{ fontFamily: "Fredoka" }}
                    data-testid="stat-max-btc"
                  >
                    {profile.max_btc_held || 0}
                  </p>
                  <p className="text-xs text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{t("profile.stats.maxBtc")}</p>
                </div>
                <div className="bg-[#E3F2FD] dark:bg-[#0a1428] rounded-2xl p-3">
                  <Star className="w-5 h-5 mx-auto mb-1 text-[#38B6FF]" />
                  <p
                    className="text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD]"
                    style={{ fontFamily: "Fredoka" }}
                    data-testid="stat-favorite"
                  >
                    {profile.favorite_crypto || "-"}
                  </p>
                  <p className="text-xs text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{t("profile.stats.favorite")}</p>
                </div>
              </div>
            </div>

            {/* Missions */}
            <div className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] flex items-center gap-2" style={{ fontFamily: "Fredoka" }}>
                  <Award className="w-5 h-5 text-[#FFC107]" /> {t("profile.missions.title")}
                </h2>
                <span className="text-xs font-semibold text-[#1A237E]/50 dark:text-[#E3F2FD]/50">
                  {t("profile.missions.summary", { done: Object.values(profile.missions || {}).filter(m => m.completed).length, total: Object.keys(profile.missions || {}).length })}
                </span>
              </div>
              <div className="space-y-3">
                {Object.entries(profile.missions || {}).map(([key, mission]) => {
                  const info = MISSION_LABELS[key] || { labelKey: null, icon: Target };
                  const Icon = info.icon;
                  const pct = Math.min(100, (mission.progress / mission.target) * 100);
                  const label = info.labelKey ? t(info.labelKey) : key;
                  return (
                    <div key={key}
                      className={`rounded-2xl p-4 border-2 transition-all ${
                        mission.completed
                          ? "bg-[#00E676]/10 border-[#00E676]/40"
                          : "bg-white/50 dark:bg-slate-900/50 border-[#1A237E]/10 dark:border-[#E3F2FD]/10"
                      }`}
                      data-testid={`mission-${key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Icon className={`w-4 h-4 shrink-0 ${mission.completed ? "text-[#00C853]" : "text-[#1A237E]/50 dark:text-[#E3F2FD]/50"}`} />
                          <span className="text-sm font-semibold text-[#1A237E] dark:text-[#E3F2FD] truncate">{label}</span>
                        </div>
                        <span className={`text-xs font-bold shrink-0 ml-2 ${mission.completed ? "text-[#00C853]" : "text-[#38B6FF]"}`}>{t("profile.missions.xpReward", { count: mission.xp_reward })}</span>
                      </div>
                      <div className="h-2 w-full bg-[#1A237E]/10 dark:bg-[#E3F2FD]/10 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ease-out ${
                            mission.completed ? "bg-[#00E676]" : "bg-[#38B6FF]"
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-[#1A237E]/50 dark:text-[#E3F2FD]/50 mt-1.5 font-semibold">
                        {mission.completed ? t("profile.missions.completed") : t("profile.missions.progress", { progress: mission.progress, target: mission.target })}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Appearance + Notifications used to live here too — moved to
            SettingsPage so each setting has exactly one home. Profile
            stays focused on identity, progress, and danger-zone. */}

        {/* Badges */}
        {profile && (
          <div
            className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6 mt-6"
            data-testid="badges-block"
          >
            <h2
              className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-3 flex items-center gap-2"
              style={{ fontFamily: "Fredoka" }}
            >
              <Award className="w-5 h-5 text-[#FFC107]" />
              {t("profile.badges.title")}
            </h2>
            <p className="text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-4">
              {t("profile.badges.description", {
                earned: (profile.badges || []).length,
              })}
            </p>
            <BadgesGrid earnedBadges={profile.badges || []} />
          </div>
        )}

        {/* Danger zone — GDPR export + account deletion */}
        <div className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-[#FF5A5F]/30 shadow-xl rounded-3xl p-6 mt-6" data-testid="danger-zone">
          <h2 className="text-lg font-bold text-[#FF5A5F] mb-1" style={{ fontFamily: "Fredoka" }}>
            {t("profile.dangerZone.title")}
          </h2>
          <p className="text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-4">
            {t("profile.dangerZone.description")}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={handleLogoutEverywhere}
              disabled={loggingOutAll}
              className="rounded-xl"
              data-testid="logout-all-btn"
            >
              <ShieldOff className="w-4 h-4 mr-2" />
              {loggingOutAll ? t("profile.button.loggingOutAll") : t("profile.button.logoutAll")}
            </Button>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={exporting}
              className="rounded-xl"
              data-testid="export-data-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? t("profile.button.exporting") : t("profile.button.exportData")}
            </Button>
            <Button
              variant="outline"
              onClick={() => { setShowDeleteModal(true); setConfirmText(""); setConfirmPassword(""); setDeleteError(""); }}
              className="rounded-xl text-[#FF5A5F] border-[#FF5A5F]/40 hover:bg-[#FF5A5F]/10"
              data-testid="open-delete-account-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t("profile.button.deleteAccount")}
            </Button>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="delete-account-modal"
        >
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-bold text-[#FF5A5F] mb-2" style={{ fontFamily: "Fredoka" }}>
              {t("profile.deleteModal.title")}
            </h3>
            <p className="text-sm text-[#1A237E]/70 dark:text-[#E3F2FD]/70 mb-4">
              {t("profile.deleteModal.description")}
            </p>
            <label className="block text-xs font-semibold text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-1">
              {t("profile.deleteModal.confirmInstructions")} <span className="font-mono">{t("profile.deleteModal.confirmText")}</span> {t("profile.deleteModal.confirmInstructionsSuffix")}
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full bg-white dark:bg-slate-800 text-[#1A237E] dark:text-[#E3F2FD] border-2 border-[#1A237E]/20 dark:border-[#E3F2FD]/20 rounded-xl px-3 py-2 mb-3 font-mono"
              placeholder={t("profile.deleteModal.confirmText")}
              data-testid="delete-confirm-input"
              autoComplete="off"
            />
            <label className="block text-xs font-semibold text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-1">
              {t("profile.deleteModal.passwordLabel")} <span className="font-normal">{t("profile.deleteModal.passwordHint")}</span>
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full bg-white dark:bg-slate-800 text-[#1A237E] dark:text-[#E3F2FD] border-2 border-[#1A237E]/20 dark:border-[#E3F2FD]/20 rounded-xl px-3 py-2 mb-3"
              placeholder={t("profile.deleteModal.passwordPlaceholder")}
              data-testid="delete-password-input"
              autoComplete="current-password"
            />
            {deleteError && (
              <p className="text-xs text-[#FF5A5F] mb-3" data-testid="delete-error">{deleteError}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="rounded-xl"
                data-testid="delete-cancel-btn"
              >
                {t("profile.deleteModal.button.cancel")}
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting || confirmText !== "DELETE"}
                className="rounded-xl bg-[#FF5A5F] text-white hover:bg-[#e64a4f]"
                data-testid="delete-confirm-btn"
              >
                {deleting ? t("profile.deleteModal.button.deleting") : t("profile.deleteModal.button.confirm")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Compact pill next to the user's email showing whether the account
 * is verified. Google-OAuth accounts are always verified upstream
 * (the OAuth callback gates on `email_verified=true`), so we render
 * a green check for them too. For email/password accounts, an
 * unverified state shows an amber badge with a Resend button so
 * the user can re-trigger the verification email without leaving
 * the profile page.
 */
function EmailVerificationBadge({ user, t }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (user?.email_verified) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-bold"
        data-testid="email-verified-badge"
        title={t("profile.verification.verifiedTitle")}
      >
        <BadgeCheck className="w-3 h-3" aria-hidden="true" />
        {t("profile.verification.verified")}
      </span>
    );
  }

  // Google accounts shouldn't ever land here (they auto-verify),
  // but if user.email_verified is missing for some reason, hide the
  // unverified state for Google to avoid a confusing nudge.
  if (user?.auth_type === "google") return null;

  const handleResend = async () => {
    if (sending || sent || !user?.email) return;
    setSending(true);
    try {
      await authService.resendVerification(user.email);
      setSent(true);
      toast.success(t("profile.verification.resendTitle"), {
        description: t("profile.verification.resendDescription", { email: user.email }),
      });
    } catch {
      // axios interceptor toasts the error
    }
    setSending(false);
  };

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 px-2 py-0.5 text-[10px] font-bold"
      data-testid="email-unverified-badge"
    >
      <MailWarning className="w-3 h-3" aria-hidden="true" />
      {t("profile.verification.unverified")}
      <button
        type="button"
        onClick={handleResend}
        disabled={sending || sent}
        className="underline underline-offset-2 disabled:no-underline disabled:opacity-60 ml-1"
        data-testid="profile-resend-verification-btn"
      >
        {sent ? t("profile.verification.sent") : sending ? t("profile.verification.resending") : t("profile.verification.resend")}
      </button>
    </span>
  );
}
